import type { ExportStatus } from '../models/export';
import type { ThreePlReportId } from '../models/reports';

const STORAGE_KEY_PREFIX = 'three_pl_excel_export_pending';
export const PENDING_EXPORT_TTL_MS = 30 * 60 * 1000;

export interface StoredThreePlExport {
  exportId: string;
  reportId: ThreePlReportId;
  dateFrom?: string;
  dateTo?: string;
  invoiceId?: number;
  invoiceLabel?: string;
  startedAt: number;
  status: ExportStatus | 'starting';
  urlFile?: string | null;
}

function storageKey(teamId: number): string {
  return `${STORAGE_KEY_PREFIX}:${teamId}`;
}

export function isExportInProgress(status: StoredThreePlExport['status']): boolean {
  return status === 'starting' || status === 'queued' || status === 'processing';
}

export function isExportReadyToDownload(exportSession: StoredThreePlExport | null): boolean {
  return exportSession?.status === 'completed' && Boolean(exportSession.urlFile?.trim());
}

export function saveStoredExport(teamId: number, data: StoredThreePlExport): void {
  try {
    localStorage.setItem(storageKey(teamId), JSON.stringify(data));
  } catch {
    // ignore quota / private mode
  }
}

export function updateStoredExportStatus(teamId: number, status: StoredThreePlExport['status']): void {
  const current = loadStoredExport(teamId);
  if (!current) return;
  saveStoredExport(teamId, { ...current, status });
}

export function loadStoredExport(teamId: number | null | undefined): StoredThreePlExport | null {
  if (teamId == null) return null;

  try {
    const raw = localStorage.getItem(storageKey(teamId));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredThreePlExport;
    if (!parsed.exportId?.trim() || !parsed.reportId || !parsed.startedAt) {
      clearStoredExport(teamId);
      return null;
    }

    if (Date.now() - parsed.startedAt > PENDING_EXPORT_TTL_MS) {
      clearStoredExport(teamId);
      return null;
    }

    if (parsed.status === 'completed' && !parsed.urlFile?.trim()) {
      clearStoredExport(teamId);
      return null;
    }

    return parsed;
  } catch {
    clearStoredExport(teamId);
    return null;
  }
}

export function clearStoredExport(teamId: number | null | undefined): void {
  if (teamId == null) return;
  try {
    localStorage.removeItem(storageKey(teamId));
  } catch {
    // ignore
  }
}

export function getStoredExportRemainingMs(exportSession: StoredThreePlExport): number {
  return Math.max(0, PENDING_EXPORT_TTL_MS - (Date.now() - exportSession.startedAt));
}
